import { useContext, useEffect, useState } from "react";
import { Routes, Route, useLocation, useNavigate } from "react-router-dom";
import Dashboard from "./components/Dashboard";
import { Navigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import UserContext from "./components/User/UserContext";
import Nav from "./components/Nav";
import { AuthLoginPage } from "./components/auth0/AuthLoginPage";
import Templates from "./components/template/Templates";
import PermissionsMap from "./components/User/PermissionsMap";
import PermissionGuard from "./components/User/PermissionGuard";
import TemplateForm from "./components/template/TemplateForm";
import WordBank from "./components/wordbank/WordBank";
import Orders from "./components/shop/orders/Orders";
import Order from "./components/shop/orders/Order";
import Project from "./components/project/Project";
import SearchProjects from "./components/project/SearchProjects";
import BreakBar from "./components/Clocking/Break/BreakBar";
import { IoArrowBackCircle } from "react-icons/io5";
import Cart from "./components/shop/cart/Cart";
import Fleet from "./components/fleet/Fleet";
import Attendance from "./components/Clocking/Attendance/Attendance";
import ExportForm from "./components/project/Export/ExportForm";
import TemplateSelection from "./components/templateselection/TemplateSelection";
import Report from "./components/reports/Report";
import Reports from "./components/reports/Reports";
import ReportV2 from "./components/reports/v2/ReportV2";
import TrainingDropBoxView from "./components/dashboard/TrainingDropBoxView";
import ClockingNotesAdminPage from "./components/Clocking/ClockingNotesAdminPage";
import LoadingCircle from "./components/utility/LoadingCircle";
import PublicProjectHome from "./components/public/PublicProjectHome";
import { pdfjs } from "react-pdf";

import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import AppDownloadPage from "./components/AppDownloadPage";
import PublicCart from "./components/public/PublicCart";
import { MessengerProvider, Chat, UserChatsSplitView, Log } from "@1stle/messenger-library";
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@5.3.31/build/pdf.worker.min.mjs`;

function App() {
	const { user, isLoading, isAuthenticated, getAccessTokenSilently } = useAuth0();
	const { setUser, setToken, api, token, userPermissions, loadingUserPermissions } = useContext(UserContext);
	const [hasUnsavedFormData, setHasUnsavedFormData] = useState(false);
	const navigate = useNavigate();
	let location = useLocation();

	useEffect(() => {
		if (!isLoading && user) {
			setUser(user);
			getAccessTokenSilently().then((newToken) => {
				setToken(newToken);
			});
		}
	}, [getAccessTokenSilently, isLoading, setToken, setUser, user]);

	const clickBackButton = () => {
		if (typeof hasUnsavedFormData !== "undefined" && hasUnsavedFormData === true) {
			let confirmationResult = window.confirm("You have unsaved changes, are you sure you want to leave?");
			if (confirmationResult === true) {
				setHasUnsavedFormData(false);
				navigate(-1);
			}
		} else {
			navigate(-1);
		}
	};

	if (isLoading) {
		return null;
	}

	return (
		<MessengerProvider
			token={token}
			baseTitle="Krummy Capture"
			apiUrl={process.env.REACT_APP_MESSENGER_URL}
			canSeeAllLogs={userPermissions.includes(PermissionsMap.SEE_ALL_LOGS)}
			canSeeDeletedMessages={userPermissions.includes(PermissionsMap.SEE_DELETED_MESSAGES)}>
			<div className="App">
				{isAuthenticated ? <Nav hasUnsavedFormData={hasUnsavedFormData} setHasUnsavedFormData={setHasUnsavedFormData} /> : null}
				{loadingUserPermissions ? (
					<div className="flex justify-center items-center h-[60vh] flex-col gap-2">
						<div className="flex justify-center items-center flex-col gap-2 bg-white border border-black p-5 rounded-lg shadow-lg">
							<LoadingCircle />
							<p>Loading...</p>
						</div>
					</div>
				) : (
					<>
						{isAuthenticated ? (
							<>
								{api !== process.env.REACT_APP_PRODUCTION_URL ? (
									<div className="p-2 font-bold text-center warning-banner-pulse z-50">
										<p>! This is a testing environment. Your changes will not be saved to the actual site. !</p>
									</div>
								) : null}
								<div className="">
									{location.pathname === "/" || location.pathname === "/dashboard" ? null : (
										<div className="p-3 flex items-center gap-2 bg-gray-300 ">
											<IoArrowBackCircle className="text-3xl cursor-pointer" onClick={() => clickBackButton()} />
											<p className="cursor-pointer" onClick={() => clickBackButton()}>
												Go back
											</p>
										</div>
									)}
								</div>

								<PermissionGuard permission={PermissionsMap.CAN_CLOCK_IN_AND_OUT}>
									<BreakBar api={api} token={token} />
								</PermissionGuard>
							</>
						) : null}

						<Routes>
							<Route path="/public/project/:id" element={<PublicProjectHome />} />
							<Route path="/public/cart/:id" element={<PublicCart />} />
							{!isAuthenticated ? (
								<Route path="*" element={<AuthLoginPage />} />
							) : (
								<>
									<Route path="/" element={<Navigate to="/dashboard" />} />
									<Route path="/login" element={<AuthLoginPage />} />
									<Route path="/dashboard" element={<Dashboard />} />

									<Route
										path="/templates"
										element={
											<PermissionGuard permission={PermissionsMap.TEMPLATES_READ}>
												<Templates />
											</PermissionGuard>
										}
									/>

									<Route
										path="/template/:id"
										element={
											<PermissionGuard permission={PermissionsMap.TEMPLATES_READ}>
												<TemplateForm />
											</PermissionGuard>
										}
									/>

									<Route
										path="/wordbank"
										element={
											<PermissionGuard permission={PermissionsMap.WORD_BANK_READ}>
												<WordBank />
											</PermissionGuard>
										}
									/>

									<Route
										path="/messages"
										element={<UserChatsSplitView user={user} odooId={user["https://claim.krummycapture/claim"].odoo_id} />}
									/>

									<Route
										path="/cart"
										element={
											<PermissionGuard permission={PermissionsMap.ODOO_ORDERS}>
												<Cart token={token} api={api} />
											</PermissionGuard>
										}
									/>

									<Route
										path="/orders"
										element={
											<PermissionGuard permission={PermissionsMap.ODOO_ORDERS}>
												<Orders token={token} api={api} />
											</PermissionGuard>
										}
									/>
									<Route
										path="/order/:id"
										element={
											<PermissionGuard permission={PermissionsMap.ODOO_ORDERS}>
												<Order token={token} api={api} />
											</PermissionGuard>
										}
									/>

									<Route
										path="/clocking-notes"
										element={
											<PermissionGuard permission={PermissionsMap.VIEW_CLOCKING_NOTES}>
												<ClockingNotesAdminPage token={token} api={api} />
											</PermissionGuard>
										}
									/>

									<Route
										path="/export-form"
										element={
											<PermissionGuard permission={PermissionsMap.CAN_EXPORT}>
												<ExportForm api={api} token={token} />
											</PermissionGuard>
										}
									/>

									<Route path="/projects" element={<SearchProjects />} />
									<Route path="/projects/search" element={<SearchProjects api={api} token={token} />} />
									<Route path="/odoo-project/:odooid/template-select" element={<TemplateSelection api={api} token={token} />} />

									<Route path="/project/:id" element={<Project />} />

									<Route
										path="/reports"
										element={
											<PermissionGuard permission={PermissionsMap.REPORTS_READ}>
												<Reports api={api} token={token} />
											</PermissionGuard>
										}
									/>
									<Route
										path="/report/v2/:reportId"
										element={
											<PermissionGuard permission={PermissionsMap.REPORTS_READ}>
												<ReportV2
													api={api}
													token={token}
													hasUnsavedFormData={hasUnsavedFormData}
													setHasUnsavedFormData={setHasUnsavedFormData}
												/>
											</PermissionGuard>
										}
									/>
									<Route
										path="/report/:reportId"
										element={
											<PermissionGuard permission={PermissionsMap.REPORTS_READ}>
												<Report
													api={api}
													token={token}
													hasUnsavedFormData={hasUnsavedFormData}
													setHasUnsavedFormData={setHasUnsavedFormData}
												/>
											</PermissionGuard>
										}
									/>

									<Route path="/fleet/:id" element={<Fleet api={api} token={token} title={"Fleet Dropbox Folder"} type="fleet" />} />
									<Route path="/design/:id" element={<Fleet api={api} token={token} title={"Design Dropbox Folder"} type="design" />} />
									<Route
										path="/construction/:id"
										element={<Fleet api={api} token={token} title={"Construction Dropbox Folder"} type="construction" />}
									/>
									<Route path="/service/:id" element={<Fleet api={api} token={token} title={"Service Dropbox Access"} type="service" />} />

									<Route path="/attendance" element={<Attendance token={token} api={api} />} />

									<Route path="/training" element={<TrainingDropBoxView api={api} title={"Company Training Drop Box Access"} />} />

									<Route path="/log/:logId" element={<Log user={user} />} />

									<Route path="/app-download" element={<AppDownloadPage />} />
								</>
							)}
						</Routes>
					</>
				)}
			</div>
		</MessengerProvider>
	);
}

export default App;
